import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import { Select, SelectOption, Image } from "uikit-react";
import UIkit from "uikit";
import classnames from "classnames";

import { AppContexts } from "../../providers/";
import { useCookies } from "react-cookie";
import FeatherIcon from "../common/FeatherIcon";
import { useDebounce } from "../../hook";
import NewMessageService from "../../services/new-message.service";
import { extractThreadMetaTokens } from "../../providers/inbox-state";
import { buildMessageSearchResults } from "./search-results";

import { SMSThreadCard } from "./thread-cards/sms.js";
import { AirbnbThreadCard } from "./thread-cards/airbnb.js";
import { ReservationThreadCard } from "./thread-cards/reservation.js";

const searchValue = {
  search_term: "",
  filters: "all",
  preset: "",
  start_date: "2000-01-01",
  end_date: "2050-12-31",
  end_type: "check_in",
  checked_in: false,
  checked_out: false,
  categories: "",
  type: "message",
};

const ContactSearchThreadCard = ({ contact, selected, handleSelect }) => {
  const iconName = contact.category || "owner";

  return (
    <div
      className={classnames("chat-guest-contact-wrapper", {
        "active-chat-guest-contact": selected,
      })}
      key={`contact-search-${contact.thread_id || contact.id}`}
      onClick={() => handleSelect(contact.thread_id)}
    >
      <div className="message-text-group">
        <Image src={`/images/${iconName}-active.svg`} />
        <div>
          <div className="chat-guest-contact-name">
            {contact.first_name} {contact.last_name}
          </div>
          <div className="chat-guest-contact-date">
            {contact.formatted_phone || contact.phone || contact.email || "Contact"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ({ onNewMessage } = {}) => {
  const inboxContext = useContext(AppContexts.InboxContext);
  const { listReservations, searchReservation } = useContext(
    AppContexts.ReservationsContext,
  );

  const { user } = useContext(AppContexts.AuthContext);
  const { notification } = useContext(AppContexts.UtilContext);
  const { setNotes } = useContext(AppContexts.NotesContext);
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  const [option, setOption] = useState("");
  const [search, setSearch] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollContainerRef = useRef(null);
  const contactSearchRequestIdRef = useRef(0);
  const inboxActionsRef = useRef({});
  const reconciliationInFlightRef = useRef(false);
  const lastReconciliationAtRef = useRef(0);
  const normalizedSearch = search.trim();
  const debouncedSearch = useDebounce(normalizedSearch, 400);
  const {
    inbox,
    selectedThreadId,
    setSelectedThreadId,

    selectPaginatedThread,
    selectPersonalPaginatedThread,
    getPaginatedThreadDetail,
    getPersonalPaginatedThreadDetail,
    setThreadDetail,

    threadCnt,
    setReservation,
    isMetaPending,

    legacy,
    setLegacy,
  } = inboxContext;
  const history = useHistory();

  // A new query replaces the provider's canonical collection.
  useEffect(() => {
    setPageNo(0);
  }, [option, recordsPerPage, legacy]);

  useEffect(() => {
    let active = true;
    const loadPage = !!(user.user.personal_inboxes || [])[0]
      ? inboxContext.personalInbox(
          user.user.personal_inboxes[0].id,
          pageNo,
          option,
          recordsPerPage,
        )
      : inboxContext.refresh(pageNo, option, recordsPerPage);

    Promise.resolve(loadPage)
      .catch(() => {
        if (active) UIkit.notification("Unable to refresh the inbox.", "error");
      })
      .finally(() => {
        if (active) setLoadingMore(false);
      });

    return () => {
      active = false;
    };
  }, [option, recordsPerPage, pageNo, legacy]);

  const hasMore = (inbox || []).length < threadCnt;

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      setLoadingMore(true);
      setPageNo((prev) => prev + 1);
    }
  }, [loadingMore, hasMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const personalInboxId = useMemo(() => {
    if (!!(user.user.personal_inboxes || [])[0]) {
      return user.user.personal_inboxes[0].id;
    }

    return null;
  }, [user]);

  inboxActionsRef.current = {
    personalInbox: inboxContext.personalInbox,
    refresh: inboxContext.refresh,
  };

  useEffect(() => {
    let active = true;

    const reconcile = (force = false) => {
      if (!active || document.visibilityState === "hidden") return;

      const now = Date.now();
      if (
        reconciliationInFlightRef.current ||
        (!force && now - lastReconciliationAtRef.current < 5000)
      ) {
        return;
      }

      reconciliationInFlightRef.current = true;
      lastReconciliationAtRef.current = now;
      const request =
        personalInboxId !== null
          ? inboxActionsRef.current.personalInbox(
              personalInboxId,
              0,
              option,
              recordsPerPage,
              true,
            )
          : inboxActionsRef.current.refresh(
              0,
              option,
              recordsPerPage,
              true,
            );

      Promise.resolve(request)
        .catch(() => {})
        .finally(() => {
          reconciliationInFlightRef.current = false;
        });
    };

    const reconcileNow = () => reconcile(true);
    const reconcileWhenVisible = () => {
      if (document.visibilityState === "visible") reconcileNow();
    };
    const interval = window.setInterval(() => reconcile(), 30000);

    window.addEventListener("websocket-connect", reconcileNow);
    window.addEventListener("inbox-reconcile", reconcileNow);
    window.addEventListener("focus", reconcileNow);
    document.addEventListener("visibilitychange", reconcileWhenVisible);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("websocket-connect", reconcileNow);
      window.removeEventListener("inbox-reconcile", reconcileNow);
      window.removeEventListener("focus", reconcileNow);
      document.removeEventListener(
        "visibilitychange",
        reconcileWhenVisible,
      );
    };
  }, [personalInboxId, option, recordsPerPage, legacy]);

  const toggleLegacy = () => {
    setPageNo(0);
    setLegacy(!legacy);
  };

  const handleSelect = (threadId) => {
    // let current = +window.location.pathname.split("/")[2];

    if (threadId === null) {
      history.push("/messages/new");
      setSelectedThreadId("new");
      setThreadDetail({});
    } else if (+selectedThreadId !== +threadId) {
      history.push(`/messages/${threadId}`);
      if (personalInboxId !== null) {
        selectPersonalPaginatedThread(threadId, personalInboxId);
      } else {
        selectPaginatedThread(threadId);
      }
    }

    // pushToRoom("update_status", {
    //   thread_id: threadId,
    //   personal_inbox_id: personalInboxId,
    //   status: "viewing",
    // });

    setNotes([]);
  };

  const handleSelectOption = (e) => {
    setPageNo(0);
    setOption(e.target.value);
  };

  const { reservations, contacts: searchableContacts } = useMemo(
    () => buildMessageSearchResults(listReservations, contactResults),
    [listReservations, contactResults],
  );

  const hasActiveSearch = debouncedSearch.length >= 2;

  const handleMetaCallback = async (result, thread_id, token) => {
    if (result) {
      let message;
      const tokens = extractThreadMetaTokens(result.thread);
      if (token === "unread") {
        message = null;
      } else if (token === "priority") {
        message = null;
      } else {
        message = tokens.includes("archived")
          ? "Message Archived"
          : "Message Unarchived";
      }
      if (message) {
        UIkit.notification(message, "success");
      }

      if (searchValue.search_term) {
        searchReservation(searchValue);
      }
    }
  };

  useEffect(() => {
    if (debouncedSearch.length < 2) {
      searchValue.search_term = "";
      contactSearchRequestIdRef.current += 1;
      setContactResults([]);
      return;
    }

    const currentSearch = debouncedSearch;
    searchValue.search_term = currentSearch;
    searchReservation({ ...searchValue, searchKey: "messages-inbox" });

    const requestId = contactSearchRequestIdRef.current + 1;
    contactSearchRequestIdRef.current = requestId;

    NewMessageService.searchContacts(currentSearch)
      .then((res) => {
        if (requestId !== contactSearchRequestIdRef.current) {
          return;
        }

        const paginated = res.data?.paginated || res.data || [];
        setContactResults(paginated.filter((contact) => !!contact?.thread_id));
      })
      .catch(() => {
        if (requestId !== contactSearchRequestIdRef.current) {
          return;
        }

        setContactResults([]);
      });
  }, [debouncedSearch, searchReservation]);

  useEffect(() => {
    if (!!notification) {
      if (personalInboxId !== null) {
        inboxContext.personalInbox(
          personalInboxId,
          0,
          option,
          recordsPerPage,
          true,
        ).catch(() => {});
      } else {
        inboxContext.refresh(0, option, recordsPerPage, true).catch(() => {});
      }
      if (+notification.thread_id === +window.location.pathname.split("/")[2]) {
        if (personalInboxId !== null) {
          getPersonalPaginatedThreadDetail(
            notification.thread_id,
            personalInboxId,
          );
        } else {
          getPaginatedThreadDetail(notification.thread_id);
        }
      }
    }
  }, [notification]);

  const handleGuest = (e) => {
    setSearch(e.target.value);
  };

  const getThreadType = (thread) => {
    if (!thread.sources) {
      return "sms";
    }

    let sources = thread.sources.map((s) => s.ota);
    if (sources.includes("airbnb")) {
      return "airbnb";
    }

    return "sms";
  };

  const togglePersonalMeta = async (thread_id, token) => {
    try {
      let result = null;
      if (!!(user.user.personal_inboxes || [])[0]) {
        result = await inboxContext.togglePersonalMeta(
          thread_id,
          user.user.personal_inboxes[0].id,
          token,
        );
      } else {
        result = await inboxContext.setMeta(thread_id, {
          thread_id,
          token: token,
        });
      }

      await handleMetaCallback(result, thread_id, token);
    } catch (error) {
      UIkit.notification("Unable to update the conversation.", "error");
    }
  };

  const setPersonalMeta = async (thread_id, token, set) => {
    try {
      let result = null;
      if (!!(user.user.personal_inboxes || [])[0]) {
        result = await inboxContext.setPersonalMeta(
          thread_id,
          user.user.personal_inboxes[0].id,
          token,
          set,
        );
      } else {
        result = await inboxContext.setMeta(thread_id, {
          thread_id,
          token: token,
          state: set,
        });
      }

      await handleMetaCallback(result, thread_id, token);
    } catch (error) {
      UIkit.notification("Unable to update the conversation.", "error");
    }
  };

  return (
    <aside
      className="uk-card uk-card-default uk-card-small uk-card-body uk-width-1-4@m hms-msg-cards chat-guest-contact-card"
      style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      {!+user?.user?.owner_id && (
        <div className="inboxSelector">
          <button
            className={`uk-button hms-btn hms-blue-btn ${legacy ? "hms-blue-btn-transparent" : ""}`}
            onClick={toggleLegacy}
          >
            My Inbox
          </button>
          <button
            className={`uk-button hms-btn hms-blue-btn ${!legacy ? "hms-blue-btn-transparent" : ""}`}
            onClick={toggleLegacy}
          >
            All Messages
          </button>
        </div>
      )}
      <div className="chat-msg-type">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Select onChange={handleSelectOption}>
            <SelectOption value="">Inbox</SelectOption>
            <SelectOption value="priority">Priority</SelectOption>
            <SelectOption value="unread">Unread</SelectOption>
            <SelectOption value="archived">Archived</SelectOption>
            <SelectOption value="all">All Messages</SelectOption>
          </Select>
          {onNewMessage && (
            <span
              style={{
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
              }}
              onClick={onNewMessage}
              uk-tooltip="New Message"
            >
              <FeatherIcon name="plus-circle" className="inbox-new-msg-icon" />
            </span>
          )}
        </div>
        <div className="uk-search uk-search-default">
          {!search ? (
            <span className="uk-search-icon-flip" uk-search-icon="true"></span>
          ) : (
            <span
              className="uk-close-icon uk-close-icon-flip"
              uk-icon="close"
              onClick={() => {
                handleGuest({ target: { value: "" } });
              }}
            ></span>
          )}
          <input
            className="uk-search-input"
            type="search"
            placeholder="Name or Property ID"
            value={search}
            onChange={handleGuest}
          />
        </div>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="chat-guest-contact"
          ref={scrollContainerRef}
          style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
        >
          {normalizedSearch === "" ? (
            (inbox || []).length === 0 && !loadingMore ? (
              <div className="uk-margin-top">
                There are no messages in this folder
              </div>
            ) : (
              <>
                {inbox?.map((thread) => {
                  let threadType = getThreadType(thread);
                  if (threadType === "sms") {
                    return (
                      <SMSThreadCard
                        key={thread.id}
                        thread={thread}
                        selected={thread.id === selectedThreadId}
                        handleSelect={handleSelect}
                        togglePersonalMeta={togglePersonalMeta}
                        setPersonalMeta={setPersonalMeta}
                        isMetaPending={isMetaPending}
                      />
                    );
                  } else if (threadType === "airbnb") {
                    return (
                      <AirbnbThreadCard
                        key={thread.id}
                        thread={thread}
                        selected={thread.id === selectedThreadId}
                        handleSelect={handleSelect}
                        togglePersonalMeta={togglePersonalMeta}
                        setPersonalMeta={setPersonalMeta}
                        isMetaPending={isMetaPending}
                      />
                    );
                  }
                  return null;
                })}

                {loadingMore && (
                  <div className="uk-text-center uk-margin-small">
                    <div uk-spinner="ratio: 0.8"></div>
                  </div>
                )}
              </>
            )
          ) : !hasActiveSearch ? (
            <div className="uk-margin-top">Type at least 2 characters to search</div>
          ) : reservations?.length === 0 && searchableContacts.length === 0 ? (
            <div className="uk-margin-top">
              There are no messages in this folder
            </div>
          ) : (
            <>
              {reservations?.map((reservation) => {
                return (
                  <ReservationThreadCard
                    key={
                      reservation.thread_id ||
                      reservation.guest_reservation_id ||
                      reservation.id
                    }
                    reservation={reservation}
                    selected={reservation.thread_id === selectedThreadId}
                    handleSelect={handleSelect}
                    togglePersonalMeta={togglePersonalMeta}
                    setPersonalMeta={setPersonalMeta}
                    isMetaPending={isMetaPending}
                    setReservation={setReservation}
                  />
                );
              })}
              {searchableContacts.map((contact) => {
                return (
                  <ContactSearchThreadCard
                    key={`contact-${contact.thread_id}`}
                    contact={contact}
                    selected={contact.thread_id === selectedThreadId}
                    handleSelect={handleSelect}
                  />
                );
              })}
              {loadingMore && (
                <div className="uk-text-center uk-margin-small">
                  <div uk-spinner="ratio: 0.8"></div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </aside>
  );
};
